import {CollisionRect, WORKSPACE_DEFAULT_HEIGHT, WORKSPACE_DEFAULT_WIDTH} from './check-workspaces-collision.utility';

export type RoomPolygon = number[][];

export interface RoomBoundary {
  id?: string;
  polygon?: RoomPolygon | null;
}

type Point = {x: number; y: number};

const getRotatedRectCorners = (
  rectX: number,
  rectY: number,
  rectWidth: number,
  rectHeight: number,
  rotate: number,
): Point[] => {
  const cx = rectX + rectWidth / 2;
  const cy = rectY + rectHeight / 2;
  const rad = (rotate * Math.PI) / 180;
  const corners = [
    {x: rectX, y: rectY},
    {x: rectX + rectWidth, y: rectY},
    {x: rectX + rectWidth, y: rectY + rectHeight},
    {x: rectX, y: rectY + rectHeight},
  ];

  return corners.map((point) => {
    const dx = point.x - cx;
    const dy = point.y - cy;
    return {
      x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
      y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
    };
  });
};

const polygonArea = (polygon: RoomPolygon): number => {
  if (polygon.length < 3) {
    return 0;
  }

  let area = 0;
  let previous = polygon[polygon.length - 1];
  for (const current of polygon) {
    area += previous[0] * current[1] - current[0] * previous[1];
    previous = current;
  }
  return Math.abs(area) / 2;
};

export const pointInPolygon = (point: Point, polygon: RoomPolygon): boolean => {
  if (polygon.length < 3) {
    return false;
  }

  const {x, y} = point;
  let inside = false;
  let previous = polygon[polygon.length - 1];

  for (const current of polygon) {
    const xi = previous[0];
    const yi = previous[1];
    const xj = current[0];
    const yj = current[1];
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi || Number.EPSILON) + xi;
    if (intersects) {
      inside = !inside;
    }
    previous = current;
  }

  return inside;
};

const sampleEdgePoints = (a: Point, b: Point, samplesPerEdge: number): Point[] => {
  const points: Point[] = [];
  for (let i = 0; i <= samplesPerEdge; i += 1) {
    const t = i / samplesPerEdge;
    points.push({
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
    });
  }
  return points;
};

export const isOrientedRectInsidePolygon = (
  rect: CollisionRect,
  polygon: RoomPolygon,
  samplesPerEdge: number = 8,
): boolean => {
  if (!polygon || polygon.length < 3) {
    return false;
  }

  const corners = getRotatedRectCorners(rect.x, rect.y, rect.width, rect.height, rect.rotateDegree);
  for (let i = 0; i < corners.length; i += 1) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    for (const point of sampleEdgePoints(a, b, samplesPerEdge)) {
      if (!pointInPolygon(point, polygon)) {
        return false;
      }
    }
  }

  return true;
};

export const findSmallestRoomContainingPoint = (point: Point, rooms: RoomBoundary[]): RoomBoundary | undefined => {
  const matches = rooms
    .filter((room) => room.polygon && room.polygon.length >= 3 && pointInPolygon(point, room.polygon))
    .map((room) => ({room, area: polygonArea(room.polygon as RoomPolygon)}));

  if (!matches.length) {
    return undefined;
  }

  return matches.reduce((smallest, current) => (current.area < smallest.area ? current : smallest)).room;
};

/**
 * Client-side room-bounds check result.
 * Mirrors backend: room by desk center, then full footprint coverage.
 * - ok: valid placement (or no room polygons to validate against)
 * - outsideRoom: desk center is not inside any room (corridor / outside plan)
 * - crossesWall: room found, but footprint sticks through walls
 */
export type WorkspaceRoomBoundsResult = 'ok' | 'outsideRoom' | 'crossesWall';

export const checkWorkspaceRoomBounds = (
  candidate: CollisionRect,
  rooms: RoomBoundary[],
): WorkspaceRoomBoundsResult => {
  const roomsWithPolygon = rooms.filter((room) => room.polygon && room.polygon.length >= 3);
  if (!roomsWithPolygon.length) {
    return 'ok';
  }

  const center = {
    x: candidate.x + candidate.width / 2,
    y: candidate.y + candidate.height / 2,
  };
  const room = findSmallestRoomContainingPoint(center, roomsWithPolygon);
  if (!room?.polygon) {
    return 'outsideRoom';
  }

  return isOrientedRectInsidePolygon(candidate, room.polygon) ? 'ok' : 'crossesWall';
};

export const isWorkspaceFullyInsideRoom = (candidate: CollisionRect, rooms: RoomBoundary[]): boolean => {
  return checkWorkspaceRoomBounds(candidate, rooms) === 'ok';
};

export const workspaceCenterToCollisionRect = (
  coordinates: Array<number[]>,
  rotateDegree: number,
  width: number = WORKSPACE_DEFAULT_WIDTH,
  height: number = WORKSPACE_DEFAULT_HEIGHT,
): CollisionRect => ({
  x: coordinates[0][0] - width / 2,
  y: coordinates[0][1] - height / 2,
  width,
  height,
  rotateDegree,
});
